import { Component, OnInit, HostListener, OnDestroy, Output, EventEmitter, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { of, Subject, Subscription } from 'rxjs';
import { UserService } from '../../data/service/user.service';
import { OrganizationService } from '../../data/service/organization.service';
import { ToasterMessageService } from '../../shared/toaster-message/toaster-message.service';
import { SharedService } from 'src/app/core/service/shared.service';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { debounceTime, distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/service/auth/auth.service';
import { DomSanitizer } from '@angular/platform-browser';
import { SearchService } from 'src/app/data/service/search.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { SidebarType } from 'src/app/modules/bookkeeping/enums/sidebar-type';
import { environment } from 'src/environments/environment';
import { Guid } from 'guid-typescript';
import { StripePaymentService } from 'src/app/data/service/stripePayment.service';
import { finalize, repeat } from 'rxjs/operators';
import { RefreshLog_Type } from '../../data/enums/common-enum';
import { ManageIntercom } from '../../core/util/manage-intercom';
import { RecognizedInvoiceService } from 'src/app/modules/bookkeeping/services/recognized-invoice.service';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { HttpInternalService } from 'src/app/data/service/http.service';
import { HttpClient } from '@angular/common/http';
import { BillsToPaySupplierDetailsRequest } from 'src/app/modules/bookkeeping/models/invoice/bills-to-pay-invoice-details-request';
import { RecognizedSupplierService } from 'src/app/modules/payable/services/recognized-supplier.service';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { fromCamelCase } from 'src/app/core/util/camel-case-converter';
import { GodModeService } from 'src/app/data/service/god-mode.service';
import { ErrorRecord } from 'src/app/modules/bookkeeping/models/error/error-record';
import { ErrorsService } from 'src/app/data/service/errors.service';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ErrorRecordType } from 'src/app/modules/bookkeeping/models/error/error-record-type';
import { AppConfigService } from 'src/app/appConfig.service';
import { ExpenseSearchHit, TransactionSearchHit } from 'src/app/data/model/search-result';
import { TransactionLedgerRow } from 'src/app/data/model/transaction-ledger';
import { TransactionsService } from 'src/app/data/service/transactions.service';
import { OrganizationSwitchService } from 'src/app/core/service/organization-switch.service';
import { PracticeMembership, PracticeService } from 'src/app/data/service/practice.service';
import { pickPractice } from 'src/app/modules/clients/client-view';

export type StatusType = 'Awaiting Payment' | 'Paid' | 'Voided';
export type IntegrationType = 'Xero' | 'QBO' | 'None';

// A single invoice that failed during processing, surfaced in the header
// notifications bell. Placeholder shape until wired to the real errors API.
export interface InvoiceProcessingError {
  id: string;
  invoiceNumber: string;
  supplierName: string;
  amount: number;
  stage: string;        // where it failed, e.g. "Recognition", "Publish"
  errorMessage: string; // human-readable reason
  failedAt: Date;
  jsonData: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})

export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
  showToolTipDelay: number = 400;
  subscriptions: Subscription[] = [];

  currentUser: any;
  sortedOrganizationList: any = [];
  organizationList: any;
  searchValue: string = null;
  isDropdownOpen = false;

  /** SD-1248: the user's practices, totalled, for the switcher's "All clients" row. Null if none. */
  practiceOverview: { clientCount: number; needsActionCount: number; logoUrl: string | null } | null = null;

  // Search
  isSearchResultsShown: boolean = false;
  searchTerm: string = '';
  isSearchBarShown: boolean = false;

  isSidebarVisible: boolean = false;
  isSettingsSidebar: boolean = false;
  isUploadSidebar: boolean = false;
  types = SidebarType;

  private organizationIdFromUrl: string = null;
  private _unsubscribe$ = new Subject<void>();
  gameSeed: any;
  private integrationType: IntegrationType = 'None';

  emailInboxes: string[] = [];
  hoveredInbox: string = null;
  copiedInbox: string = null;

  companyEmail: string | null = null;
  personalEmail: string | null = null;
  roleId: string = null;

  // SD-1137: surface the connected accounting integration in the user
  // dropdown so users always know what Synced is talking to.
  accounting: {
    connected: boolean;
    platform: 'Xero' | 'QBO' | null;
    tenantName: string | null;
    lastSyncedAt: string | null;
  } = { connected: false, platform: null, tenantName: null, lastSyncedAt: null };
  accountingLoading: boolean = true;
  isSearchShow: boolean = false;
  isMobileDevice: boolean = false;

  // ── Invoice processing error notifications ─────────────────────────────
  // Bell dropdown in the header surfaces invoices that failed to process so
  // the user can review the detail and resubmit. On a successful resubmit the
  // notification is removed from the list.
  // NOTE: placeholder data for now — wire to the real errors API later.
  processingErrors: ErrorRecord[] = [];
  resubmittingErrorIds: Set<string> = new Set<string>();

  @ViewChild('billsSection') billsSection: ElementRef;
  @ViewChild('suppliersSection') suppliersSection: ElementRef;

  @Output() showSideNav: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showContactForm = new EventEmitter<boolean>();
  showSupplierBar: boolean = false;
  private contactIsSaved: boolean = false;

  @HostListener('document:click', ['$event'])
  clickout() {
    this.isDropdownOpen = false;
  }

  closeSearch() {
    if (this.isMobileDevice)
      this.isSearchBarShown = false;
  }

  constructor(
    public organizationService: OrganizationService,
    private userService: UserService,
    public spinnerService: SpinnerService,
    public sharedService: SharedService,
    private router: Router,
    private suppliersService: SuppliersService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _authService: AuthService,
    private readonly _sanitizer: DomSanitizer,
    private readonly _searchService: SearchService,
    private readonly _activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private stripePaymentService: StripePaymentService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private readonly _invoiceService: InvoiceService,
    private http: HttpClient,
    private syncedHttp: HttpInternalService,
    private supplierRecognizeService: RecognizedSupplierService,
    private godModeService: GodModeService,
    private _errorsService: ErrorsService,
    private configService: AppConfigService,
    private readonly _transactionsService: TransactionsService,
    private readonly organizationSwitch: OrganizationSwitchService,
    private readonly practiceService: PracticeService
  ) {
    super();
    this.checkIfMobileDevice();
  }

  toggleSidebar(sidebar: SidebarType) {
    if (sidebar === SidebarType.settings)
      this.isSettingsSidebar = !this.isSettingsSidebar;
    else if (sidebar === SidebarType.upload)
      this.isUploadSidebar = !this.isUploadSidebar;

    this.isSidebarVisible = !this.isSidebarVisible;
  }

  /** The + button: the upload panel, which also offers coding an expense by hand. */
  openUploadSidebar() {
    this.isSettingsSidebar = false;
    this.isUploadSidebar = true;
    this.isSidebarVisible = true;
  }

  // SD-1137: pull the accounting integration state (uses the same source as
  // the Settings → Integrations page via Account/GetSetupChecklistState).
  // Refreshed on org switch via setData().
  loadAccountingStatus(): void {
    const orgId = this.organizationService.selectedOrganization
      || localStorage.getItem('SelectedOrganization');
    if (!orgId) return;
    this.accountingLoading = true;
    this.syncedHttp.getRequest<any>('Account/GetSetupChecklistState', { organizationId: orgId })
      .subscribe(
        (res) => {
          const d = res?.data || {};
          this.accounting = {
            connected: !!d.accountingConnected,
            platform: (d.accountingPlatform === 'Xero' || d.accountingPlatform === 'QBO') ? d.accountingPlatform : null,
            tenantName: d.accountingTenantName || null,
            lastSyncedAt: d.accountingLastSyncedAt || null
          };
          this.accountingLoading = false;
        },
        () => {
          // silent — header still functional without this badge
          this.accountingLoading = false;
        }
      );
  }

  ngOnInit() {
    ManageIntercom.updateintercode(true, this.configService);
    this.loadProcessingErrors();

    // A supplier's name clicked in a side panel (Inbox, Bills to Pay, Payments, the dashboard,
    // Banking): the supplier panel opens over that panel.
    this.supplierRecognizeService.openPanel$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.supplierPanelOver = true;
        this.showSupplierBar = true;
      });

    // ✅ Listen for sidebar reload trigger — refresh role + org dropdown
    this.sharedService.reloadSidebar$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.reloadHeaderData();
      });

    this.organizationSwitch.switched$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => this.onSwitched());

    this.practiceService.me$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(me => {
        this.practices = me.practices;
        this.practiceOverview = me.practices.length === 0 ? null : {
          clientCount: me.practices.reduce((n, p) => n + (p.clientCount || 0), 0),
          needsActionCount: me.practices.reduce((n, p) => n + (p.needsActionCount || 0), 0),
          // The practice's logo when there is one practice; several share the grid icon.
          logoUrl: me.practices.length === 1 ? me.practices[0].logoUrl || null : null
        };
      });

    const tempOrgIdParam = this._activatedRoute.snapshot.queryParamMap?.get('organizationId');

    if (tempOrgIdParam && Guid.isGuid(tempOrgIdParam)) {
      this.organizationIdFromUrl = tempOrgIdParam;
      this.organizationService.selectedOrganization = this.organizationIdFromUrl;
      sessionStorage.organizationId = this.organizationIdFromUrl;
    }

    this.subscriptions.push(this.organizationService.organizationList.subscribe(data => {
      if (data) {
        // One entry per organisation: the list comes back once per role held, so two roles in one
        // organisation (your own and a practice's grant, say) listed it twice.
        const seen = new Set<string>();
        data = data.filter(o => {
          const id = String((o && o.organisationID) || '').toLowerCase();
          if (!id || seen.has(id)) { return !id; }
          seen.add(id);
          return true;
        });
        this.organizationList = data.sort((a, b) => {
          if (a.orderRank === 0 && b.orderRank !== 0) return 1;  // a goes after b
          if (a.orderRank !== 0 && b.orderRank === 0) return -1; // a goes before b
          return a.orderRank - b.orderRank; // normal ascending sort
        });
        this.sortedOrganizationList = this.organizationList.slice(0, 5);
        this.setData();
        this.loadOrganisationLogos();
      } else {
        this.organizationList = [];
      }
    }));
    this.setIntegrationType();
    this.userService.getUserGetByIdServer().subscribe(response => {
      const role = response?.data?.role;
      this.roleId = (role !== undefined && role !== null) ? role.toString() : null;
    }, err => {
      console.warn('Header: failed to load user role', err);
      this.roleId = null;
    });
    this.subscriptions.push(this.userService.loggedUserDetails.subscribe(userdata => {
      if (userdata) {
        this.currentUser = userdata;
        this.setUser(userdata);
        this.setData();
      } else {
        this.currentUser = [];
      }
    }));

    this.fetchOrganizationEmail();
  }

  // ✅ Add this new method
  private reloadHeaderData(): void {
    // Reload user role
    this.userService.getUserGetByIdServer().subscribe(response => {
      if (response && response.data) {
        this.roleId = response.data.role.toString();
        this.userService.setUserRoleData(response.data.role);
      }
    }, err => {
      console.warn('Header: failed to reload user role', err);
    });
  }

  fetchOrganizationEmail() {
    const orgId = this.organizationService.selectedOrganization;
    if (!orgId) return;

    const userId = localStorage.getItem("Id") || sessionStorage.Id || sessionStorage.UserId;
    this.http.get<any>(`${this.configService.apiURL}Organisation/getOrganizationEmail?id=${orgId}&userId=${userId}`)

      .subscribe(
        (res: any) => {
          // Support both new and old API response formats
          if ('companyEmail' in res || 'personalEmail' in res) {
            this.companyEmail = res.companyEmail || null;
            this.personalEmail = res.personalEmail || null;
          } else if ('email' in res) {
            this.companyEmail = res.email;
            this.personalEmail = null;
          } else {
            this.companyEmail = null;
            this.personalEmail = null;
          }
        },
        err => {
          this.companyEmail = null;
          this.personalEmail = null;
        }
      );
  }

  setUser(response) {
    var id = this._activatedRoute.snapshot.queryParamMap?.get('organizationId');
    if (id != null) {
      sessionStorage.UserName = response.name;
      sessionStorage.Id = response.id;
      sessionStorage.UserId = response.userId;
      localStorage.setItem('UserName', response.name);
      localStorage.setItem('UserId', response.userId);
      localStorage.setItem('Id', response.id);
      localStorage.setItem('SelectedOrganization', response.organizationId);
      localStorage.setItem('Email', response.email);
      localStorage.setItem('CreatedAt', response.created_date);
      this.userService.setUserRoleData(response.data.user.role);
      this.userService.upUserDetails = JSON.stringify(response);
      this.roleId = response.data.user.role;

      if (response.organizationId) {
        sessionStorage.organizationId = response.organizationId;
        sessionStorage.selectedOrganization = response.organizationId;
        this.AddandUpdatesupplierXeroData(response.organizationId, response.email)
      }
    }
  }

  setData() {
    if (this.organizationList && this.organizationList.length > 0 && this.organizationService.selectedOrganizationName === null) {
      const getOrganization = this.organizationList.find(x => x.organisationID.toLowerCase() == this.currentUser?.organizationId.toLowerCase());
      if (getOrganization) {
        this.organizationService.selectedOrganization = getOrganization.organisationID;
        this.organizationService.selectedOrganizationName = getOrganization.organisationName;
        this.organizationService.OrganizationUseAzureAPI = getOrganization.useAzureAPI;
        this.organizationService.selectedOrganizationDomen = getOrganization.accountingIntegration == 0 ? 'Xero' : 'QBO';
        this.organizationService.refreshConnectionURL = getOrganization.refreshConnectionURL;
        this.organizationService.baseURL = getOrganization.baseURL;
        this.organizationService.connectURL = getOrganization.connectURL;
        this.currentUser.organizationId = getOrganization.organisationID;
        this.currentUser.picture = '../../../assets/img/user-icon.png';
        this.organizationService.getSelectedOrganization(this.organizationService.selectedOrganization);
        this.organizationService.selectedOrganizationDateFormat = getOrganization.dateFormat;

        if (this.organizationIdFromUrl && this.organizationIdFromUrl != getOrganization.organisationID) {
          this.selected(this.organizationIdFromUrl);
        }
      }
    } else {
      const getOrganization = this.organizationList.find(x => x.organisationID.toLowerCase() == this.organizationService.selectedOrganization.toLowerCase());
      if (getOrganization) {
        this.organizationService.selectedOrganization = getOrganization.organisationID;
        this.organizationService.selectedOrganizationName = getOrganization.organisationName;
        this.organizationService.OrganizationUseAzureAPI = getOrganization.useAzureAPI;
        this.organizationService.selectedOrganizationDomen = getOrganization.accountingIntegration == 0 ? 'Xero' : 'QBO';
        this.organizationService.getSelectedOrganization(this.organizationService.selectedOrganization);
        this.organizationService.selectedOrganizationDateFormat = getOrganization.dateFormat;

        if (this.organizationIdFromUrl && this.organizationIdFromUrl != getOrganization.organisationID) {
          this.selected(this.organizationIdFromUrl);
        }
      }
    }

    // SD-1137: refresh the accounting badge in the user dropdown whenever the
    // selected org changes.
    this.loadAccountingStatus();

  }

  /** Each organisation's logo, by lower-case id, for the menu; organisations without one show initials. */
  orgLogos: { [organisationId: string]: string } = {};
  private logosSub: Subscription | null = null;
  private logosLoadedFor = '';

  /** Once per list of organisations: the kept copy draws at once, the fresh one follows. */
  private loadOrganisationLogos(): void {
    const ids = this.organizationList.map(o => (o.organisationID || '').toLowerCase()).sort().join(',');
    if (!ids || ids === this.logosLoadedFor) {
      return;
    }
    this.logosLoadedFor = ids;
    if (this.logosSub) { this.logosSub.unsubscribe(); }
    this.logosSub = this.practiceService.organisationLogos()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(logos => this.orgLogos = logos);
  }

  orgLogo(org: { organisationID: string }): string | null {
    return (org && org.organisationID && this.orgLogos[org.organisationID.toLowerCase()]) || null;
  }

  /** An image that will not load (removed, or a dead website icon) falls back to initials. */
  dropOrgLogo(org: { organisationID: string }): void {
    if (org && org.organisationID) {
      delete this.orgLogos[org.organisationID.toLowerCase()];
    }
  }

  /** SD-1248: the user's practices, for Practice view's nav (Team is a manager's). */
  practices: PracticeMembership[] = [];

  /** The practice Practice view is showing: the one in the address, as the page itself picks it. */
  private get currentPractice(): PracticeMembership | null {
    const requested = this.router.parseUrl(this.router.url || '/').queryParams['practice'];
    return pickPractice(this.practices, requested);
  }

  get isPracticeManager(): boolean {
    const practice = this.currentPractice;
    return !!practice && practice.role === 'manager';
  }

  /** Keeps the practice being shown when moving between Practice view's pages. */
  get practiceQuery(): { practice?: string } {
    const practice = this.currentPractice;
    return practice ? { practice: practice.organisationId } : {};
  }

  /** SD-1248: on /clients and /clients/team the caption names the practice view, not a client. */
  get isOnAllClients(): boolean {
    const path = (this.router.url || '').split('?')[0];
    return path === '/clients' || path.indexOf('/clients/') === 0;
  }

  openDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    this.clearSearchValue();
  }

  /**
   * SD-1248: the switch itself is OrganizationSwitchService's now, so the All clients page can
   * switch too. What stays here is the dropdown's own business: closing it, clearing its search,
   * and going to Home afterwards. The connection / document-limit notices and the Stripe sync run
   * from onSwitched(), for a switch made from anywhere.
   */
  selected(organizationId: any): void {
    // When the organisation is in the list, switchTo applies the selection synchronously -- the
    // dropdown below closes on an organisation that is already selected, as it always has.
    const switching = this.organizationSwitch.switchTo(organizationId);

    this.isDropdownOpen = false;
    let element: HTMLElement = document.getElementById("orgdropdown") as HTMLElement;
    element.click();
    this.clearSearchValue();

    switching.then(() => {
      if (!this.organizationIdFromUrl) {
        this.router.navigate(['/dashboard'], { replaceUrl: true });
      }
    });
  }

  /** After any organisation switch: the checks the header has always run on the new organisation. */
  private onSwitched(): void {
    this.getOrganizationConnectedStatus();
    this.getOrganizationDocumentLimit();
    this.SyncStripePlansToDatabase();
  }

  // ── Invoice processing error notifications ─────────────────────────────

  /**
   * Load the invoices that failed to process. Placeholder data for now —
   * replace with a call to the real errors API when available.
   */
  loadProcessingErrors(): void {
    this.processingErrors = [];
    this._errorsService.getErrorRecordsByOrganizationId(this.organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (recordsData: ErrorRecord[]) => {
          this.processingErrors = recordsData;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message)
        }
      )
  }
  /** Number shown on the bell badge. */
  get processingErrorCount(): number {
    return this.processingErrors.length;
  }

  /** True while a given error is being resubmitted (disables its button). */
  isResubmitting(id: string): boolean {
    return this.resubmittingErrorIds.has(id);
  }

  /**
   * Resubmit a failed invoice for processing. On success the notification is
   * removed from the list. Placeholder: simulates an async call for now.
   */
  resubmitProcessingError(error: InvoiceProcessingError, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.resubmittingErrorIds.has(error.id)) {
      return;
    }
    this.resubmittingErrorIds.add(error.id);

    // TODO: replace with real resubmit API call.
    if (this.isGuid(error.jsonData)) {
      this._invoiceService.reSubmitInvoice(error.jsonData, this.organizationService.selectedOrganization).pipe(take(1)).subscribe();
    }
    setTimeout(() => {
      this.resubmittingErrorIds.delete(error.id);
      this.processingErrors = this.processingErrors.filter(e => e.id !== error.id);
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Success,
        'Resubmitted',
        `Invoice resubmitted for processing.`
      );
    }, 1200);
  }

  /**
   * Download the source document for a failed invoice. Placeholder for now —
   * wire to the real document-download endpoint when available.
   */
  downloadProcessingError(error: InvoiceProcessingError, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isGuid(error.jsonData)) {
      const link = this.renderer.createElement('a');
      link.setAttribute('target', '_blank');
      link.setAttribute('href', this._invoiceService.getDownloadInvoiceUrl(error.jsonData, ""));
      link.setAttribute('download', "Invoice.pdf");
      link.click();
      link.remove();
    }
    // TODO: replace with real document download (e.g. invoiceService.downloadFile).
    this._toasterMessageService.displayToasterState(
      ToasterTypes.Success,
      'Download',
      `Downloading document for ${error.invoiceNumber}…`
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(x => x.unsubscribe());
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  AddandUpdatesupplierXeroData(orgId, email) {
    this.suppliersService.AddandUpdatesupplierXeroData(orgId, email, RefreshLog_Type.Auto)
      .pipe(take(1))
      .subscribe(data => {

      });
  }

  godModeUser: any = null;
  signOut() {
    const storedUser = localStorage.getItem('GodModeUser');
    if (storedUser) {
      this.godModeUser = JSON.parse(storedUser);
    }
    if (this.godModeUser) {
      this.userService.exitGodMode({ userId: this.godModeUser.id, organizationId: this.currentUser.organizationId })
        .pipe(take(1))
        .subscribe(() => {
          this.godModeService.exitGodMode();
          localStorage.removeItem('isImpersonating');
          localStorage.removeItem('GodModeUser');
        });
    }
    this._authService.signOut().subscribe(() => {
      //this.router.navigate(['/auth/login']);
    });
    this.gameSeed = setInterval(() => {//your code to navigate }, 100);

      clearInterval(this.gameSeed);
      this.gameSeed = null;
    });
    // this.router.navigate(['auth/login']);
  }

  /** The admin portal is its own app now (Synced Admin), on its own site. */
  openAdminPortal() {
    window.open(environment.adminAppUrl, '_blank', 'noopener');
  }

  redirectToHelpCenter() {
    window.open('https://help.syncedhq.com/', '_blank');
  }

  // ── AI chat panel ──
  showAiPanel: boolean = false;
  aiPanelInitialQuery: string = '';

  /** SD-1244: the user's AI setting in this organisation (super admins always); the API enforces it too. */
  get canUseAi(): boolean {
    return !!(this.currentUser?.aiUser || this.currentUser?.role === 1);
  }

  openAiPanelWithQuery(query: string): void {
    if (!this.canUseAi) { return; }
    this.aiPanelInitialQuery = query;
    this.showAiPanel = true;
    this.isSearchBarShown = false;
  }

  // ── Search results, opened in a side panel over the page the user is on ──
  showSearchExpensePanel: boolean = false;
  searchExpenseColorClass: string = '';
  /** Published bills open read-only, as the inbox opens them; the form's Edit action unlocks them. */
  searchExpenseEditable: boolean = true;
  showSearchTransactionPanel: boolean = false;
  /** ActivityComponent pages through an array, so one transaction is an array of one. */
  searchTransactions: any[] = [];
  private _searchTransactionRequest: Subscription = null;

  openSearchExpense(hit: ExpenseSearchHit): void {
    // The inbox's own record view is already an expense form listening for the invoice sent
    // below: opening a panel over it would swap the record underneath too. Go to the record.
    if (/^\/inbox(\/(view|edit)Invoice|.*[?&]id=)/.test(this.router.url)) {
      // Through /inbox first so a viewInvoice already open reloads for the new id.
      this.router.navigateByUrl('/inbox').then(() =>
        this.router.navigate(['/inbox/viewInvoice', hit.invoiceId], { replaceUrl: true }));
      return;
    }

    this.closeSearchTransaction();
    this.showSupplierBar = false;
    this.searchExpenseColorClass = hit.hexColorClass || '';
    this.searchExpenseEditable = !hit.processed;
    this.showSearchExpensePanel = true;

    this._invoiceService.getInvoiceById(hit.invoiceId)
      .pipe(take(1))
      .subscribe(
        invoice => {
          // The form is created with the panel; give it a moment to subscribe (as the dashboard does).
          setTimeout(() => {
            if (this.showSearchExpensePanel) { this._recognizedInvoiceService.sendExistingInvoice(invoice); }
          }, 300);
        },
        () => {
          this.showSearchExpensePanel = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', 'That expense could not be opened.');
        });
  }

  closeSearchExpense(): void {
    this.showSearchExpensePanel = false;
  }

  openSearchTransaction(hit: TransactionSearchHit): void {
    this.closeSearchTransaction();
    this.showSearchExpensePanel = false;
    this.showSupplierBar = false;

    // The search result is a summary. The reconcile panel wants the ledger's row, so read the
    // ledger narrowed to this transaction's day, account and amount and pick the row out of it.
    const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(hit.transactionDate || '');
    if (!day) {
      this.showSearchTransaction(this.ledgerRowFromHit(hit));
      return;
    }
    const date = new Date(+day[1], +day[2] - 1, +day[3]);
    const size = Math.abs(hit.amount);

    this._searchTransactionRequest = this._transactionsService
      .getLedger(
        this.organizationService.selectedOrganization, hit.accountId, date, date,
        { toReconcile: true, reconciled: true }, '', 1, 500, 'date', 'asc', null,
        { accountIds: [], txn: '', codedTo: [], details: '', report: '', notInReport: false, amountMin: size, amountMax: size })
      .pipe(take(1))
      .subscribe(
        ledger => {
          const id = (hit.transactionId || '').toLowerCase();
          const row = (ledger?.rows || []).find(r => (r.unreconciledReportId || '').toLowerCase() === id);
          this.showSearchTransaction(row || this.ledgerRowFromHit(hit));
        },
        () => this.showSearchTransaction(this.ledgerRowFromHit(hit)));
  }

  closeSearchTransaction(): void {
    if (this._searchTransactionRequest) {
      this._searchTransactionRequest.unsubscribe();
      this._searchTransactionRequest = null;
    }
    this.showSearchTransactionPanel = false;
    this.searchTransactions = [];
  }

  private showSearchTransaction(row: TransactionLedgerRow): void {
    this._searchTransactionRequest = null;
    this.searchTransactions = [row];
    this.showSearchTransactionPanel = true;
  }

  /** What the panel reads off a ledger row, from what the search already knows. */
  private ledgerRowFromHit(hit: TransactionSearchHit): TransactionLedgerRow {
    return {
      unreconciledReportId: hit.transactionId,
      accountID: hit.accountId,
      accountName: hit.accountName,
      transactionDate: hit.transactionDate,
      description: hit.description,
      amount: hit.amount,
      type: hit.type,
      status: '',
      isReconciled: hit.isReconciled,
      importMethod: '',
      reportID: null,
      relatedID: hit.relatedId,
      relatedName: hit.relatedName,
      runningBalance: null
    };
  }

  // ── How-to guides ──
  showGuidesModal: boolean = false;
  guidesSearch: string = '';
  guidesCategory: string = 'All';
  selectedGuide: any = null;
  guideCategories: string[] = ['All', 'Getting Started', 'Expenses', 'Bills', 'Statements', 'Settings'];
  guides = [
    { videoId: 'dQw4w9WgXcQ', title: 'Getting started with Synced', category: 'Getting Started', duration: '3:24', description: 'A complete walkthrough of the Synced dashboard and core features.' },
    { videoId: 'dQw4w9WgXcQ', title: 'How to add an expense', category: 'Expenses', duration: '2:45', description: 'Upload receipts and bills and let AI handle the coding for you.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Coding expenses with AI', category: 'Expenses', duration: '4:10', description: 'Learn how the AI agent automatically assigns account codes to expenses.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Approving and reviewing expenses', category: 'Expenses', duration: '3:50', description: 'Set up approval workflows and review flagged expenses.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Paying overdue bills', category: 'Bills', duration: '3:55', description: 'How to view, schedule, and pay outstanding bills.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Managing due soon bills', category: 'Bills', duration: '2:30', description: 'Stay on top of upcoming bills and avoid late payments.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Reconciling bank statements', category: 'Statements', duration: '5:30', description: 'Match transactions and resolve unmatched bank statement entries.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Using the AI reconciliation agent', category: 'Statements', duration: '4:15', description: 'Let the AI auto-match transactions and save hours every month.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Connecting Xero or QuickBooks', category: 'Settings', duration: '6:20', description: 'Sync your accounting platform with Synced in a few clicks.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Inviting team members', category: 'Settings', duration: '2:10', description: 'Add staff, set roles, and manage access permissions.' },
    { videoId: 'dQw4w9WgXcQ', title: 'Setting up your organisation', category: 'Settings', duration: '3:00', description: 'Configure your organisation details, billing, and preferences.' },
  ];

  get filteredGuides() {
    return this.guides.filter(g => {
      const matchCat = this.guidesCategory === 'All' || g.category === this.guidesCategory;
      const matchSearch = !this.guidesSearch || g.title.toLowerCase().includes(this.guidesSearch.toLowerCase()) || g.description.toLowerCase().includes(this.guidesSearch.toLowerCase());
      return matchCat && matchSearch;
    });
  }

  openGuide(videoId: string) {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
  }

  getSafeVideoUrl(videoId: string) {
    return this._sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&color=white`
    );
  }

  searchOrganization(value: string) {
    this.searchValue = value;
    if (this.searchValue && this.searchValue != '') {
      this.sortedOrganizationList = this.organizationList
        .filter(a => a.organisationName.toLowerCase().includes(this.searchValue.toLowerCase()))
        .sort((a, b) => a.orderRank < b.orderRank ? 1 : a.orderRank > b.orderRank ? -1 : 0);;
    } else {
      this.sortedOrganizationList = this.organizationList.slice(0, 5);
    }

    if (this.sortedOrganizationList.length > 5)
      this.sortedOrganizationList = this.sortedOrganizationList.slice(0, 5);
  }

  clearSearchValue() {
    this.searchValue = null;
    setTimeout(() => {
      this.sortedOrganizationList = this.organizationList.slice(0, 5);
    }, 100);
  }







  clickShowSearchBarIcon() {
    this.isSearchBarShown = !this.isSearchBarShown;
  }

  clickCloseSearchBarIcon() {
    this.searchTerm = '';
    this.isSearchResultsShown = false;
    this.isSearchBarShown = false;
  }




  private getOrganizationConnectedStatus() {
    this.organizationService.GetOrganisationsDetailsByUsersLoginId(this.organizationService.selectedOrganization)
      .subscribe((data: any) => {
        if (!data?.data?.organisationConnected) {
          this.organizationService.getIntegrationCredentials(data.data.domain).subscribe((res) => {
            if (res) {
              this.organizationService.refreshConnectionURL = res.revokeEndPoint;
              this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, 'Error', `'${data.data.syncOrgName}' is not connected to ${data.data.domain}. Please, <a  class=\"toastr-link\" href=\"${this.organizationService.refreshConnectionURL}\">refresh</a> connection!`);
            }
          });
        }
        else {
          this.AddandUpdatesupplierXeroData(this.organizationService.selectedOrganization, this.currentUser.email);
        }
      }, err => {
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', err.statusText);
      });
  }

  private getOrganizationDocumentLimit() {
    this.organizationService.GetOrganisationsDocumentLimit(this.organizationService.selectedOrganization)
      .subscribe((data: any) => {
        // The API resolves the plan's limit (an override or the tier's value, else the stored
        // allowance) and says whether it is reached. Comparing orgCounts with totalDocument
        // here measured a paid organisation against its free allowance.
        if (data?.documentLimitReached) {
          this._toasterMessageService.displayToasterState(ToasterTypes.ErrorWithHtml, null,
            `You've exceeded your monthly document limit. Upgrade your subscription. <a  class=\"toastr-link\" href=\"?upgrade=documents-per-month\">Upgrade</a>`);
        }
      }, err => {
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', err.statusText);
      });
  }

  SyncStripePlansToDatabase() {
    this.stripePaymentService.SyncSubscriptionsToDatabase("StripePayment/SyncSubscriptionsToDatabase?organisationId=" + sessionStorage.organizationId + "&userId=" + this.currentUser.userId)
      .pipe(take(1),
        finalize(() => {
        }))
      .subscribe((response: any) => {
      }, err => {

      });
  }

  openSupport() {
    ManageIntercom.updateintercode(false, this.configService);
  }

  copyToClipboard(email: string, event: MouseEvent) {
    event.stopPropagation();
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(() => {
        this.copiedInbox = email;
        setTimeout(() => {
          this.copiedInbox = null;
        }, 1200);
      });
    }
  }

  /** Opened from another side panel: drawn above it rather than beside it in the stack. */
  supplierPanelOver = false;

  toggleSupplierBar(event) {
    this.showSupplierBar = !this.showSupplierBar;
    if (!this.showSupplierBar) { this.supplierPanelOver = false; }
  }

  /** A searched contact: one panel at a time. */
  openSearchContact() {
    this.showSearchExpensePanel = false;
    this.closeSearchTransaction();
    this.showSupplierBar = true;
  }

  setContactIsSaved(isSaved: boolean) {
    this.contactIsSaved = isSaved;
  }

  private setIntegrationType() {
    this.subscriptions.push(
      this.organizationService.organizationList.subscribe((data) => {
        const org = data?.find(
          (o) => o.organisationID === this.organizationService.selectedOrganization
        );
        if (org?.accountingIntegration === AccountingIntegration.xero) {
          this.integrationType = 'Xero';
        } else if (org?.accountingIntegration === AccountingIntegration.QBO) {
          this.integrationType = 'QBO';
        } else {
          this.integrationType = 'None';
        }
      })
    );
  }

  getIntegrationType(): IntegrationType {
    return this.integrationType;
  }




  checkIfMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || environment.isOutlookEnv || environment.isChromeEnv) {
      // true for mobile device
      this.isMobileDevice = true;
      this.isSearchBarShown = false;
    } else {
      this.isMobileDevice = false;
      this.isSearchBarShown = window.innerWidth > 990;
    }
  }
  isGuid(value: string): boolean {
    const guidRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

    return guidRegex.test(value);
  }
}
